import React, { useState, useEffect } from 'react';
import { addonService } from '../services/addonService';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { AvailableAddon } from '../types/subscription';
import InlinePaymentForm from './InlinePaymentForm';

interface AddonPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  addonType?: string; // Optional: pre-select specific addon
  onSuccess?: () => void;
}

const AddonPurchaseModal: React.FC<AddonPurchaseModalProps> = ({
  isOpen,
  onClose,
  addonType,
  onSuccess
}) => {
  const { subscription, hasAddon, refreshSubscription } = useSubscriptionContext();
  const [availableAddons, setAvailableAddons] = useState<AvailableAddon[]>([]);
  const [selectedAddon, setSelectedAddon] = useState<AvailableAddon | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAddons, setIsLoadingAddons] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInlinePayment, setShowInlinePayment] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAvailableAddons();
    }
  }, [isOpen]);

  useEffect(() => {
    // Pre-select addon if specified
    if (addonType && availableAddons.length > 0) {
      const addon = availableAddons.find(a => a.type === addonType);
      if (addon) {
        setSelectedAddon(addon);
      }
    }
  }, [addonType, availableAddons]);

  const loadAvailableAddons = async () => {
    try {
      setIsLoadingAddons(true);
      const addons = await addonService.getAvailableAddons();
      const planType = subscription?.plan_type;
      const filtered = addons.filter((addon) => {
        if (!addon.compatible_plans) return true;
        if (!planType) return true;
        return addon.compatible_plans.includes(planType);
      });
      setAvailableAddons(filtered);
    } catch (err) {
      console.error('Failed to load available addons:', err);
      setError('Failed to load available addons');
    } finally {
      setIsLoadingAddons(false);
    }
  };

  const handleSelectAddon = (addon: AvailableAddon) => {
    // Check if already has this addon
    if (hasAddon(addon.type)) {
      setError('You already have this addon');
      return;
    }
    setSelectedAddon(addon);
    setError(null);
    setShowInlinePayment(false);
  };

  const handlePurchase = async () => {
    if (!selectedAddon || !subscription) {
      setError('Please select an addon and ensure you have an active subscription');
      return;
    }

    if (hasAddon(selectedAddon.type)) {
      setError('You already have this addon');
      return;
    }

    if (isLoading) return;

    // Always show payment form - user must pay for addon
    setShowInlinePayment(true);
  };

  const handleInlinePaymentSuccess = async () => {
    setShowInlinePayment(false);
    setSelectedAddon(null);
    setError(null);

    // Refresh subscription to get updated addons
    refreshSubscription();

    if (onSuccess) {
      onSuccess();
    }
  };

  const handleInlinePaymentError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleInlinePaymentCancel = () => {
    setShowInlinePayment(false);
  };

  if (!isOpen) return null;

  if (!subscription || subscription.status !== 'active') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Active Subscription Required</h2>
          <p className="text-gray-600 mb-4">
            You need an active subscription to purchase addons. Please subscribe to a plan first.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Purchase Add-On</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {isLoadingAddons ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading available addons...</p>
          </div>
        ) : showInlinePayment && selectedAddon ? (
          <InlinePaymentForm
            planType={subscription.plan_type}
            planName={`${selectedAddon.name} Add-On`}
            planPrice={selectedAddon.price_dollars}
            onSuccess={handleInlinePaymentSuccess}
            onError={handleInlinePaymentError}
            onCancel={handleInlinePaymentCancel}
            isAddon={true}
            addonType={selectedAddon.type}
          />
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 bg-red-50 rounded-md border border-red-200">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <p className="text-gray-600 mb-6">
              Enhance your subscription with powerful add-ons. Add-ons are billed annually and renew with your subscription.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {availableAddons.map((addon) => {
                const hasThisAddon = hasAddon(addon.type);
                const isSelected = selectedAddon?.type === addon.type;

                return (
                  <div
                    key={addon.type}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : hasThisAddon
                        ? 'border-gray-300 bg-gray-50 opacity-75'
                        : 'border-gray-300 hover:border-blue-300 hover:shadow-md'
                    }`}
                    onClick={() => !hasThisAddon && handleSelectAddon(addon)}
                  >
                    {hasThisAddon && (
                      <div className="mb-2">
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          Active
                        </span>
                      </div>
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {addon.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">{addon.description}</p>
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-gray-900">
                        ${addon.price_dollars.toFixed(2)}
                      </span>
                      <span className="text-gray-600">/year</span>
                    </div>
                    {addon.limits?.max_scenarios && (
                      <p className="text-xs text-indigo-600 font-medium mb-2">
                        Includes {addon.limits.max_scenarios} scenarios
                      </p>
                    )}
                    {addon.features.length > 0 && (
                      <ul className="text-sm text-gray-600 space-y-1">
                        {addon.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {hasThisAddon && (
                      <p className="text-sm text-gray-500 mt-2">You already have this addon</p>
                    )}
                  </div>
                );
              })}
            </div>

            {selectedAddon && !hasAddon(selectedAddon.type) && (
              <div className="border-t pt-4">
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">{selectedAddon.name}</h4>
                  <p className="text-blue-700 mb-1">
                    <span className="text-2xl font-bold">${selectedAddon.price_dollars.toFixed(2)}</span>
                    <span className="text-sm">/year</span>
                  </p>
                  <p className="text-sm text-blue-600 mt-2">
                    Complete payment to add this addon to your subscription.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePurchase}
                    disabled={isLoading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Processing...' : 'Continue to Payment'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddonPurchaseModal;

